import {
  COOKIE_EXPIRATION_DAYS,
  CURRENCIES,
  LOCAL_STORAGE_IDS,
  STD_CODES,
  STATUS_CODES,
  VM_NOT_FOUND_MSG,
} from "constants/constants";
import { APP_ROUTES } from "constants/routes";

export const setCookie = function (
  cname,
  cvalue,
  exdays = COOKIE_EXPIRATION_DAYS
) {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
};

export const getCookie = function (cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export function hasElementInViewPort(domElement) {
  if (!domElement) {
    return false;
  }
  //Returns element geometry in respect to screen.
  const elementGeometry = domElement.getBoundingClientRect();
  const { innerWidth, innerHeight } = window;

  return (
    elementGeometry.left >= 0 &&
    elementGeometry.top >= 0 &&
    elementGeometry.right <= innerWidth &&
    elementGeometry.bottom <= innerHeight
  );
}
export const numberUptoTwoDecimals = function (number) {
  return (number = +(Math.round(number + "e+2") + "e-2"));
};

export const renderPriceWithSymbol = function (price) {
  return `${CURRENCIES.RUPEES}${numberUptoTwoDecimals(price)}`;
};

export const disablePageScroll = function () {
  //For safari, html works.
  document.querySelector("html").style.overflow = "hidden";
  return (document.body.style.overflow = "hidden");
};

export const enablePageScroll = function () {
  //For safari, html works.
  document.querySelector("html").style.overflow = "auto";
  return (document.body.style.overflow = "auto");
};

export const stopPropagationFunction = function (ev) {
  return ev.stopPropagation();
};

export const stopPropagationHOC = function (ev, wrappedFunction) {
  let args = [...arguments];

  wrappedFunction.apply(this, args);
  return ev.stopPropagation();
};

export const cacheTheCart = function (data) {
  return localStorage.setItem(
    LOCAL_STORAGE_IDS.CART_CACHE,
    JSON.stringify(data)
  );
};

export const getCartCache = function () {
  return JSON.parse(localStorage.getItem(LOCAL_STORAGE_IDS.CART_CACHE));
};

export const getVendingMachineUuid = function () {
  return localStorage.getItem(LOCAL_STORAGE_IDS.VENDING_MACHINE_ID);
};

export const setVendingMachineUuid = function (uuid) {
  return localStorage.setItem(LOCAL_STORAGE_IDS.VENDING_MACHINE_ID, uuid);
};

export const clearVendingMachineUuid = function () {
  return localStorage.removeItem(LOCAL_STORAGE_IDS.VENDING_MACHINE_ID);
};

export const getUserToken = function () {
  return getCookie(LOCAL_STORAGE_IDS.USER_TOKEN);
};

export const pageUrlHasUuid = function () {
  //URL should not be equal to any of the app route.
  let flag = Object.values(APP_ROUTES).every((route) =>
    route.url === "/"
      ? !route.url.includes(window.location.pathname)
      : !window.location.pathname.includes(route.rawUrl || route.url)
  );
  return flag;
};

//Retuns empty array if condition not satisfied, otherwise class name.
export const classNameConditional = function (condition, className) {
  return condition ? className : "";
};

//Handle change for all input changes.
export const getInputFieldNameValue = function (ev) {
  let {
    target: { name, value, type },
  } = ev;

  //If type of input is number, return value after number coercion.
  if (type === "number" && value !== "") {
    value = Number(value);
  }

  return { name, value };
};

export const getMobileNumberWithCountryCode = function (mobileNumber) {
  return `${STD_CODES.INDIA}${mobileNumber}`;
};

export const concatenateAddressComponents = (address) => {
  return `${address.address_line_1} ${
    address.address_line_2 ? address.address_line_2 : ""
  } - ${address.pincode}`;
};

export const loadScriptIntoDOM = function (src) {
  return new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    return document.body.appendChild(script);
  });
};

export const convertSecondsToMilliseconds = function (seconds) {
  return seconds * 1000;
};

export const enablePointerEvents = function () {
  return (document.body.style.pointerEvents = "auto");
};

export const disablePointerEvents = function () {
  return (document.body.style.pointerEvents = "none");
};

//Alerts user about reload confirmation for the page.
export const preventReloadPage = function () {
  return (window.onbeforeunload = function () {
    if (
      window.location.pathname.includes(APP_ROUTES.CHECKOUT_PAGE.url) ||
      window.location.pathname.includes(APP_ROUTES.DISPENSING_ITEMS.rawUrl)
    ) {
      return "Changes will be destroyed";
    }
  });
};

export const calculatePercentageValue = function (target, percentage) {
  return parseFloat(numberUptoTwoDecimals((target * percentage) / 100));
};

//Interceptor for api calls.
export const initFetchInterceptor = function (showErrorToast) {
  const componentTexts = window.appTexts.common_texts;

  const { fetch: origFetch } = window;
  window.fetch = (...args) => {
    return origFetch(...args).then((response) => {
      //Network error occured.
      if (!response) {
        showErrorToast(
          componentTexts.error_occured,
          componentTexts.network_error
        );
        return Promise.reject(componentTexts.network_error);
      }

      const clonedResponse = response.clone();
      return clonedResponse
        .json()
        .then((body) => {
          //If api response is not fulfilled, show error toast
          if (!body.success) {
            showErrorToast(componentTexts.error_occured, body.message);
          }

          //Clear local storage if uuid not found
          if (
            clonedResponse.status === STATUS_CODES.VM_NOT_FOUND &&
            body.message === VM_NOT_FOUND_MSG
          ) {
            clearVendingMachineUuid();
            return window.location.reload();
          }

          return response;
        })
        .catch((err) => err);
    });
  };
};

export const productionServer = function () {
  return process.env.NODE_ENV === "production";
};

//Allows dynamic values in a string.
//Same functionality as of template literal.
export const templateLiteralPolyfill = function (target, values) {
  //Finds out the key between "${" & "}" and then gets the value of it from values Object
  const decodeDynamicValue = function (startIndex) {
    let lastIndex = startIndex + 1;
    let key = "";

    //Key starts from startIndex + 2
    for (let i = startIndex + 2; i < target.length; i++) {
      if (target[i] === "}") {
        lastIndex = i;
        //Break once we get the key.
        break;
      } else {
        //Appends the key.
        key += target[i];
      }
    }
    return { value: values[key], lastIndex };
  };

  //final string value.
  let mappedText = "";

  for (let i = 0; i < target.length; i++) {
    //Dynamic value wraps between "${" & "}"
    if (target[i] === "$" && target[i + 1] === "{") {
      let decodedValue = decodeDynamicValue(i);
      i = decodedValue.lastIndex;
      mappedText += decodedValue.value;
    } else {
      mappedText += target[i];
    }
  }

  return mappedText;
};

//Remove duplicates from array of products.
export const removeDuplicatesFromProductsJson = function (
  data,
  key,
  concatenateStock = true
) {
  if (!data) {
    return null;
  }
  //avoid original data manipulation.
  let clonedData = JSON.parse(JSON.stringify(data));
  var uniqueData = {};

  clonedData.forEach(function (item) {
    if (!uniqueData[item[key]]) {
      uniqueData[item[key]] = item;
    } else {
      if (concatenateStock) {
        uniqueData[item[key]].stk += item.stk;
      }

      uniqueData[item[key]].quantity += item.quantity;
      uniqueData[item[key]].multipleStk = true; //for identifying if the stock is a mixture of all blocks stock.
      //dynamically setting blocked true if all same uuid cells are blocked in erp vm
      uniqueData[item[key]].blockedItem = clonedData
        .filter((ele) => ele.iUID === item.iUID)
        .every((x) => x.blockedItem === true);
    }
  });
  return Object.values(uniqueData);
};
